import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { hexToRGB, toRenderableHtml } from 'utils';
import { Container, SubWrap, ColDiv, RowDiv, Text, Pre, ContentBody } from 'styles';
import { Button, Icon, Input, Loading, Pagination, SubTitle } from 'components';
import { Alert } from 'components/modal/Alert';
import { BOARD_FAQ_MODULE_NO } from 'data/constants';

import boardService from 'services/board/board-service';

function FaqView({ list, faqList, paginationRequest, paginationResult, setPaginationRequest }) {
  const { t } = useTranslation();

  const [openContentNo, setOpenContentNo] = useState(null);
  const [details, setDetails] = useState({});
  const [password, setPassword] = useState('');
  const [alert, setAlert] = useState(null);

  // 페이지 이동 등으로 목록이 바뀌면 펼침 상태 초기화
  useEffect(() => {
    setOpenContentNo(null);
    setPassword('');
  }, [faqList]);

  const fetchDetail = async (contentNo, pwd) => {
    setDetails((prev) => ({ ...prev, [contentNo]: { ...prev[contentNo], loading: true } }));

    let data = null;

    try {
      data = await boardService.getContent(BOARD_FAQ_MODULE_NO, contentNo, pwd);
    } catch (e) {
      console.error(e);
    }

    if (data && data.canAccess) {
      setDetails((prev) => ({ ...prev, [contentNo]: { loading: false, canAccess: true, content: data.content } }));
    } else {
      setDetails((prev) => ({ ...prev, [contentNo]: { loading: false, canAccess: false, content: null } }));

      if (pwd) {
        setAlert({
          content: '비밀번호가 일치하지 않습니다.',
          confirm: {
            onClick: () => {
              setAlert(null);
            }
          }
        });
      }
    }
  };

  const toggleItem = (item) => {
    const { contentNo, useSecret } = item;

    if (openContentNo === contentNo) {
      setOpenContentNo(null);
      return;
    }

    setOpenContentNo(contentNo);
    setPassword('');

    // 이미 로드했거나 로딩 중이면 재요청하지 않음 (캐싱)
    if (details[contentNo]) {
      return;
    }

    if (useSecret) {
      setDetails((prev) => ({ ...prev, [contentNo]: { loading: false, canAccess: false, content: null } }));
    } else {
      fetchDetail(contentNo, '');
    }
  };

  const onSubmitPassword = (event, contentNo) => {
    event.preventDefault();

    if (password && password.length > 0) {
      fetchDetail(contentNo, password);
    }
  };

  const renderAnswer = (item) => {
    const { contentNo, useSecret } = item;
    const detail = details[contentNo];

    if (!detail || detail.loading) {
      return (
        <LoadingBox>
          <Loading type="inside" />
        </LoadingBox>
      );
    }

    if (!detail.canAccess) {
      if (useSecret) {
        return (
          <PasswordForm onSubmit={(event) => onSubmitPassword(event, contentNo)}>
            <RowDiv $gap={8} $mGap={4} $alignItems="center">
              <Icon name="lock" $width={24} $height={24} $padding="0" />
              <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.7)}>
                비밀글입니다. 비밀번호를 입력해 주세요.
              </Text>
            </RowDiv>
            <PasswordRow>
              <Input type="password" placeholder="비밀번호" value={password} onChange={(event) => setPassword(event.target.value)} />
              <Button label="확인" btnType="medium" btnStyle="fill" type="submit" />
            </PasswordRow>
          </PasswordForm>
        );
      }

      return (
        <Text $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.7)}>
          내용을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
        </Text>
      );
    }

    const { content } = detail;
    const comments = content.childContents || [];

    return (
      <ColDiv $gap={24} $mGap={16}>
        <Pre $type="b6" $mType="b7">
          {content.content}
        </Pre>
        {comments.map((com, index) => (
          <CommentBox key={`comment_${contentNo}_${index}`} $gap={16} $mGap={8}>
            <RowDiv $justifyContent="space-between" $alignItems="center">
              <RowDiv $gap={16} $mGap={8} $alignItems="center">
                <Icon name="comment" />
                <Text $fontWeight={500} $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.87)}>
                  {com.regUserName}
                </Text>
              </RowDiv>
              <Text $fontWeight={500} $type="b7" $mType="b8" $color={hexToRGB('#171717', 0.4)}>
                {com.regDate ? dayjs(com.regDate).format(t('date_format')) : ''}
              </Text>
            </RowDiv>
            <ContentBody $small $color={hexToRGB('#171717', 0.87)} dangerouslySetInnerHTML={{ __html: toRenderableHtml(com.content) }} />
          </CommentBox>
        ))}
      </ColDiv>
    );
  };

  return (
    <Container $gap={48}>
      {alert && <Alert {...alert} />}
      <SubWrap $type="first">
        <SubTitle title={list.subject} desc={list.subjectDesc} />
        <ColDiv $gap={24} $alignItems="flex-end" $mAlignItems="center">
          <List>
            {faqList &&
              faqList.map((item) => {
                const { contentNo } = item;
                const isOpen = openContentNo === contentNo;

                return (
                  <ListItem key={`faq_${contentNo}`}>
                    <ItemHeader onClick={() => toggleItem(item)}>
                      <TitleWrap $gap={8} $mGap={4} $alignItems="center">
                        {item.useSecret && <Icon name="lock" $width={24} $height={24} $padding="0" />}
                        <Text $type="h5" $mType="h6" $maxLine={1}>
                          {item.title}
                        </Text>
                      </TitleWrap>
                      <RowDiv $gap={24} $mGap={8} $alignItems="center">
                        <RowDiv $alignItems="center">
                          <Icon name="clock" $width={14} $height={14} />
                          <Text $color={hexToRGB('#171717', 0.4)} $fontWeight={500} $mType="b8">
                            {item.regDate ? dayjs(item.regDate).format(t('date_format')) : ''}
                          </Text>
                        </RowDiv>
                        <Icon name="arrow" $transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'} />
                      </RowDiv>
                    </ItemHeader>
                    {isOpen && <AnswerBox>{renderAnswer(item)}</AnswerBox>}
                  </ListItem>
                );
              })}
          </List>
        </ColDiv>
      </SubWrap>
      <Pagination paginationRequest={paginationRequest} paginationResult={paginationResult} setPaginationRequest={setPaginationRequest} />
    </Container>
  );
}

export default FaqView;

const List = styled.ul`
  width: 100%;
  border-top: 2px solid ${({ theme }) => theme.colors.primary900};
`;

const ListItem = styled.li`
  border-bottom: 1px solid ${({ theme }) => hexToRGB(theme.colors.strong, 0.12)};
`;

const ItemHeader = styled(RowDiv)`
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 32px 24px;
  cursor: pointer;
  transition: all 150ms ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${({ theme }) => theme.colors.fill03};
    }
  }

  @media ${({ theme }) => theme.device.mobile} {
    gap: 8px;
    padding: 16px 8px;
  }
`;

const TitleWrap = styled(RowDiv)`
  flex: 1;
  min-width: 0;
`;

const AnswerBox = styled.div`
  padding: 32px 24px;
  background-color: ${({ theme }) => theme.colors.lightest};
  border-top: 1px solid ${({ theme }) => hexToRGB(theme.colors.strong, 0.12)};

  @media ${({ theme }) => theme.device.mobile} {
    padding: 24px 16px;
  }
`;

const LoadingBox = styled.div`
  position: relative;
  min-height: 80px;
`;

const CommentBox = styled(ColDiv)`
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => hexToRGB(theme.colors.strong, 0.12)};
  border-radius: 6px;

  @media ${({ theme }) => theme.device.mobile} {
    padding: 16px;
  }
`;

const PasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;

  @media ${({ theme }) => theme.device.mobile} {
    max-width: 100%;
    gap: 8px;
  }
`;

const PasswordRow = styled(RowDiv)`
  gap: 16px;
  align-items: center;

  & > div {
    flex: 1;
  }

  & > button {
    flex-shrink: 0;
  }

  @media ${({ theme }) => theme.device.mobile} {
    gap: 8px;
  }
`;
